import { type GraphQLTaggedNode } from 'react-relay';
import { createOperationDescriptor, getRequest, type IEnvironment } from 'relay-runtime';

type Retention = {
	disposable: { dispose(): void };
	lifecycle: RelayQueryPreloadRetentionLifecycle;
	generation: number;
};

export type RelayQueryPreloadRetentionLifecycle = 'environment' | 'hook-unmount';

// Retentions are scoped to a Relay environment and normalized operation identifier, so different
// pages can remain warm without sharing or replacing each other's query records.
const retentionsByEnvironment = new WeakMap<IEnvironment, Map<string, Retention>>();

const getRetentions = (environment: IEnvironment): Map<string, Retention> => {
	const existingRetentions = retentionsByEnvironment.get(environment);
	if (existingRetentions) {
		return existingRetentions;
	}

	const retentions = new Map<string, Retention>();
	retentionsByEnvironment.set(environment, retentions);
	return retentions;
};

const releaseRetention = (
	environment: IEnvironment,
	operationIdentifier: string,
	expectedRetention?: Retention,
): boolean => {
	const retentions = retentionsByEnvironment.get(environment);
	const retention = retentions?.get(operationIdentifier);
	if (!retention || (expectedRetention && retention !== expectedRetention)) {
		return false;
	}

	retention.disposable.dispose();
	retentions?.delete(operationIdentifier);
	return true;
};

/**
 * Keeps an imperative Relay preload alive for the configured lifecycle.
 *
 * `environment` matches Apollo's persistent normalized-cache behavior: every distinct operation
 * remains retained for the lifetime of its Relay environment. `hook-unmount` releases the preload
 * retention when its matching useQueryInterop consumer unmounts.
 */
export const retainRelayQueryPreload = (
	environment: IEnvironment,
	query: GraphQLTaggedNode,
	variables: Record<string, any>,
	lifecycle: RelayQueryPreloadRetentionLifecycle,
): (() => void) => {
	const operation = createOperationDescriptor(getRequest(query), variables);
	const operationIdentifier = operation.request.identifier;
	const retentions = getRetentions(environment);
	const existingRetention = retentions.get(operationIdentifier);

	if (existingRetention) {
		existingRetention.lifecycle = lifecycle;
		existingRetention.generation += 1;
		return () => {};
	}

	const retention = {
		disposable: environment.retain(operation),
		lifecycle,
		generation: 0,
	};
	retentions.set(operationIdentifier, retention);
	const generation = retention.generation;

	return () => {
		if (retention.generation === generation) {
			releaseRetention(environment, operationIdentifier, retention);
		}
	};
};

export const releaseRelayQueryPreloadRetentionOnHookUnmount = (
	environment: IEnvironment,
	operationIdentifier: string,
): boolean => {
	const retention = retentionsByEnvironment.get(environment)?.get(operationIdentifier);
	if (retention?.lifecycle !== 'hook-unmount') {
		return false;
	}

	return releaseRetention(environment, operationIdentifier, retention);
};
